import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { openDownload } from "@/lib/downloads";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Car, Shield, AlertTriangle, FileCheck, Receipt, Sparkles, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import AIRecommendations from "@/components/AIRecommendations";
import VinRecallsBadge from "@/components/VinRecallsBadge";
import PredictiveScoreCard from "@/components/PredictiveScoreCard";
import JobTimeline from "@/components/JobTimeline";
import JobParties from "@/components/JobParties";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [genInvoice, setGenInvoice] = useState(false);
  const [letter, setLetter] = useState("");
  const [letterLoading, setLetterLoading] = useState(false);
  const [activeOp, setActiveOp] = useState(null);
  const letterRef = React.useRef(null);

  const load = () => api.get(`/jobs/${id}`).then((r) => setJob(r.data));

  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (letterLoading || letter) {
      setTimeout(() => letterRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
    }
  }, [letterLoading, letter]);

  if (!job) return <div className="text-slate-500">Loading job...</div>;

  const adas = job.adas_analysis || {};
  const vinDec = job.vin_decode || {};
  const ops = adas.required_operations || [];
  const missing = adas.missing_operations || [];

  const generateInvoices = async () => {
    const raw = window.prompt(
      "Body shop wholesale discount %\n\nThe body shop will be invoiced at retail minus this discount. They re-bill the insurance at full retail and keep the markup.\n\nDefault: 25%",
      "25",
    );
    if (raw === null) return; // user cancelled
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
      toast.error("Invalid percentage (0–100)");
      return;
    }
    setGenInvoice(true);
    try {
      await api.post(`/invoices/generate-dual/${job.id}?body_shop_discount_pct=${parsed}`);
      toast.success(`Invoices generated (Insurance retail + Body Shop ${parsed}% off)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setGenInvoice(false);
    }
  };

  const genJustification = async (opName) => {
    setActiveOp(opName);
    setLetterLoading(true);
    setLetter("");
    try {
      const res = await api.post("/claims/justification", { job_id: job.id, operation_name: opName });
      setLetter(res.data.letter);
    } catch (e) {
      toast.error("AI justification failed");
    } finally {
      setLetterLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="job-detail-page">
      <Link to="/jobs" className="text-xs text-slate-400 hover:text-blue-400 flex items-center gap-1.5 uppercase tracking-wider font-bold">
        <ArrowLeft className="w-3.5 h-3.5" /> All Jobs
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">Job · {job.id.slice(0, 8)}</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            {job.vehicle_year} {job.vehicle_make} {job.vehicle_model}
          </h1>
          <div className="font-mono text-blue-400 text-sm mt-1">{job.vin}</div>
        </div>
        <div className="flex items-center gap-3">
          <Badge className="border bg-blue-500/15 text-blue-300 border-blue-500/30 uppercase tracking-wider text-[10px]">{job.status.replace("_", " ")}</Badge>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total</div>
            <div className="font-mono text-2xl text-emerald-400">${job.total_amount.toFixed(2)}</div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <InfoCard icon={Car} label="Customer" value={job.customer_name} />
        <InfoCard icon={Shield} label="Insurer" value={job.insurance_company || "—"} sub={job.claim_number} />
        <InfoCard icon={Receipt} label="Body Shop" value={job.body_shop_name || "—"} />
        <InfoCard icon={Sparkles} label="ADAS Risk" value={(adas.risk_level || "low").toUpperCase()} accent={adas.risk_level} />
      </div>

      {(job.submitted_via_public || job.calibration_report_id || job.estimate_file_id) && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 flex flex-wrap items-center gap-3" data-testid="body-shop-submission-banner">
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-blue-300 font-bold">Body Shop Submission</div>
            <div className="text-sm text-slate-200 mt-1">
              Submitted by <span className="font-medium">{job.public_submitter_name || job.body_shop_name || "Body Shop"}</span>
              {job.public_submitter_email ? <span className="text-slate-400"> · {job.public_submitter_email}</span> : null}
            </div>
          </div>
          {job.calibration_report_id && (
            <Link to={`/calibration-reports/${job.calibration_report_id}`}>
              <Button variant="outline" className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10" data-testid="job-open-calibration-report">
                <FileCheck className="w-4 h-4 mr-1.5" /> Open Full Calibration Report
              </Button>
            </Link>
          )}
          {job.estimate_file_id && (
            <Button
              variant="outline"
              className="border-slate-700 text-slate-200 hover:bg-slate-800"
              data-testid="job-download-body-shop-pdf"
              onClick={() => openDownload(`/api/estimate-uploads/${job.estimate_file_id}`, "job_estimate_file", job.estimate_file_id)}
            >
              <Download className="w-4 h-4 mr-1.5" /> Original PDF from Body Shop
            </Button>
          )}
        </div>
      )}

      <AIRecommendations jobId={job.id} />
      <div className="grid lg:grid-cols-2 gap-4">
        <VinRecallsBadge vin={job.vin} />
        <PredictiveScoreCard jobId={job.id} />
      </div>

      {/* Iter 47 Phase 3: 4-Party model editor (owner/body_shop/insurance/payer) */}
      <JobParties jobId={job.id} />

      <Tabs defaultValue="adas" className="space-y-4">
        <TabsList data-testid="job-tabs" className="bg-[#0B1120] border border-slate-800 p-1">
          <TabsTrigger value="adas" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-300">ADAS Analysis</TabsTrigger>
          <TabsTrigger value="timeline" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-300">Timeline</TabsTrigger>
          <TabsTrigger value="vin" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-300">VIN Decode</TabsTrigger>
          <TabsTrigger value="estimate" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-300">Estimate</TabsTrigger>
          <TabsTrigger value="actions" className="data-[state=active]:bg-blue-600/20 data-[state=active]:text-blue-300">Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="adas" className="space-y-4">
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Detected Requirements</div>
                <h3 className="font-display text-xl font-medium mt-1">Required ADAS Operations</h3>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Total Cal Fees</div>
                <div className="font-mono text-xl text-cyan-400">${(adas.total_calibration_fees || 0).toFixed(2)}</div>
              </div>
            </div>
            {ops.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-8">No ADAS operations detected from estimate lines.</div>
            ) : (
              <div className="space-y-2.5">
                {ops.map((op, i) => (
                  <div key={i} data-testid={`adas-op-${i}`} className="p-4 rounded-lg bg-slate-900/40 border border-slate-800/60 hover:border-blue-500/30 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className="bg-blue-500/15 text-blue-300 border border-blue-500/30 text-[10px] uppercase tracking-wider">{op.type}</Badge>
                          <span className="font-medium">{op.name}</span>
                        </div>
                        <div className="text-xs text-slate-400 mt-1.5">System: <span className="text-slate-300">{op.system}</span></div>
                        <div className="text-xs text-slate-500 mt-2 leading-relaxed">{op.reason}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-emerald-400 text-lg">${op.fee.toFixed(2)}</div>
                        <Button size="sm" variant="outline" className="mt-2 border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50" onClick={() => genJustification(op.name)} disabled={letterLoading && activeOp === op.name} data-testid={`justify-btn-${i}`}>
                          <Sparkles className={`w-3.5 h-3.5 mr-1.5 ${letterLoading && activeOp === op.name ? "animate-spin" : ""}`} /> {letterLoading && activeOp === op.name ? "Generating..." : "AI Justify"}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {missing.length > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-amber-400">Missing Revenue Opportunities</div>
              </div>
              {missing.map((m, i) => (
                <div key={i} className="text-sm text-slate-300 py-1.5">
                  <span className="font-medium">{m.name}</span> — <span className="text-slate-500">{m.reason}</span>
                  <span className="ml-2 font-mono text-amber-400">+${m.fee.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {(letterLoading || letter) && (
            <div ref={letterRef} data-testid="justification-letter" className="rounded-xl border border-cyan-500/30 bg-[#0B1120] p-6 glow-blue">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className={`w-4 h-4 text-cyan-400 ${letterLoading ? "animate-spin" : ""}`} />
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-cyan-400">AI Justification — {activeOp}</div>
              </div>
              {letterLoading ? (
                <div className="space-y-3">
                  <div className="text-sm text-cyan-300">Calling GPT-4o... drafting OEM-compliant letter for {activeOp}</div>
                  <div className="h-2 rounded-full bg-slate-900 overflow-hidden"><div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 animate-pulse" style={{ width: "70%" }} /></div>
                </div>
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-slate-300 leading-relaxed font-sans">{letter}</pre>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="timeline">
          <JobTimeline jobId={job.id} />
        </TabsContent>

        <TabsContent value="vin">
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6">
            <h3 className="font-display text-xl font-medium mb-4">VIN Decode</h3>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3 mb-6">
              <DetailRow label="VIN" value={vinDec.vin} mono />
              <DetailRow label="Make" value={vinDec.make} />
              <DetailRow label="Model" value={vinDec.model} />
              <DetailRow label="Year" value={vinDec.year} />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-3">Detected ADAS Systems</div>
            <div className="grid md:grid-cols-2 gap-2">
              {(vinDec.adas_systems || []).map((s) => (
                <div key={s.name} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/40 border border-slate-800/50">
                  <span className="text-sm text-slate-300">{s.name}</span>
                  <Badge className={s.equipped ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" : "bg-slate-700/40 text-slate-500 border border-slate-700/40"}>
                    {s.equipped ? "EQUIPPED" : "N/A"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="estimate">
          <div className="rounded-xl border border-slate-800 bg-[#0B1120] overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-900/40">
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">#</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Op</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Description</th>
                  <th className="text-left text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Part</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Hrs</th>
                  <th className="text-right text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 px-5 py-3">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(job.estimate_lines || []).map((l) => (
                  <tr key={l.line_no} className="border-b border-slate-800/60">
                    <td className="px-5 py-3 font-mono text-xs text-slate-500">{l.line_no}</td>
                    <td className="px-5 py-3 font-mono text-xs text-blue-300">{l.operation}</td>
                    <td className="px-5 py-3 text-sm">{l.description}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-400">{l.part_number || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono text-sm">{l.hours}</td>
                    <td className="px-5 py-3 text-right font-mono text-sm text-emerald-400">${l.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="actions">
          <div className="grid md:grid-cols-2 gap-4">
            <ActionCard icon={Receipt} title="Generate Dual Invoices" description="Insurance at retail + Body Shop with wholesale discount (default 25%)" onClick={generateInvoices} loading={genInvoice} testid="generate-invoices-btn" />
            <ActionCard icon={FileCheck} title="Calibration Proof Package" description="Generate complete enterprise PDF: scans + photos + OEM + invoice" onClick={async () => {
              try {
                const r = await api.post(`/ai-ops/proof-package/${job.id}`);
                const w = window.open();
                w.document.write(`<pre style="font-family:monospace;padding:20px;white-space:pre-wrap">${r.data.markdown.replace(/</g, "&lt;")}</pre>`);
                toast.success("Proof package generated");
              } catch (e) { toast.error("Failed to generate"); }
            }} testid="proof-package-btn" />
            <ActionCard icon={FileCheck} title="View Reports" description="Calibration, scan, and OEM compliance reports" link="/reports" testid="view-reports-btn" />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const InfoCard = ({ icon: Icon, label, value, sub, accent }) => {
  const accentClass = accent === "high" ? "text-red-400" : accent === "medium" ? "text-amber-400" : "text-slate-100";
  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-600" />
      </div>
      <div className={`font-display text-lg font-medium ${accentClass}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 font-mono mt-0.5">{sub}</div>}
    </div>
  );
};

const DetailRow = ({ label, value, mono }) => (
  <div className="flex items-center justify-between border-b border-slate-800/40 pb-2">
    <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">{label}</span>
    <span className={`${mono ? "font-mono text-blue-300" : ""} text-sm`}>{value || "—"}</span>
  </div>
);

const ActionCard = ({ icon: Icon, title, description, onClick, loading, link, testid }) => {
  const content = (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-6 hover:border-blue-500/40 transition-colors cursor-pointer h-full">
      <Icon className="w-7 h-7 text-blue-400 mb-3" />
      <div className="font-medium mb-1">{title}</div>
      <div className="text-xs text-slate-500">{description}</div>
      {loading && <div className="text-xs text-cyan-400 mt-2">Processing...</div>}
    </div>
  );
  if (link) return <Link to={link} data-testid={testid}>{content}</Link>;
  return <div onClick={onClick} data-testid={testid}>{content}</div>;
};
